import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './components/auth/ProtectedRoute/ProtectedRoute';
import { lazyWithRetry, lazyWithRetryNamed } from './utils/lazyWithRetry';

// Lazy load pages with retry + stale-chunk recovery
// After a new deployment, old chunk hashes (e.g. DashboardPage.E0C_qLqC.js) no longer exist.
// lazyWithRetry catches the 404, retries once, then force-reloads the page to fetch new assets.
const AuthCallbackPage = lazyWithRetry(() => import('./pages/auth/AuthCallbackPage'));
const AccountSuspendedPage = lazyWithRetry(() => import('./pages/auth/AccountSuspendedPage'));

// Onboarding pages
const SelectPlanPage = lazyWithRetry(() => import('./pages/onboarding/SelectPlanPage'));

const DashboardPage = lazyWithRetry(() => import('./pages/dashboard/DashboardPage'));
const BrandsPage = lazyWithRetry(() => import('./pages/brands/BrandsPage'));
const DealsPage = lazyWithRetry(() => import('./pages/deals'));
const ProfilePage = lazyWithRetry(() => import('./pages/profile/ProfilePage'));
const SettingsPage = lazyWithRetry(() => import('./pages/settings/SettingsPage'));
const UsageStatsPage = lazyWithRetry(() => import('./pages/usage/UsageStatsPage'));

// Admin pages
const AdminDashboard = lazyWithRetryNamed(
  () => import('./pages/admin/AdminDashboard'),
  (m) => m.AdminDashboard
);
const UserManagement = lazyWithRetryNamed(
  () => import('./pages/admin/UserManagement'),
  (m) => m.UserManagement
);
const AnalyticsPage = lazyWithRetryNamed(
  () => import('./pages/admin/AnalyticsPage'),
  (m) => m.AnalyticsPage
);
const AuditLogViewer = lazyWithRetryNamed(
  () => import('./pages/admin/AuditLogViewer'),
  (m) => m.AuditLogViewer
);
const SKUManagement = lazyWithRetryNamed(
  () => import('./pages/admin/SKUManagement'),
  (m) => m.SKUManagement
);
const AdminLayout = lazyWithRetryNamed(
  () => import('./pages/admin/AdminLayout'),
  (m) => m.AdminLayout
);
const AdminRoute = lazyWithRetryNamed(
  () => import('./components/auth/AdminRoute'),
  (m) => m.AdminRoute
);

// Feature Request pages
const FeatureRequestsPage = lazyWithRetry(
  () => import('./pages/feature-requests/FeatureRequestsPage')
);
const MyFeatureRequestsPage = lazyWithRetry(
  () => import('./pages/feature-requests/MyFeatureRequestsPage')
);
const AdminFeatureRequestsPage = lazyWithRetryNamed(
  () => import('./pages/admin/AdminFeatureRequestsPage'),
  (m) => m.AdminFeatureRequestsPage
);

const NotFoundPage = lazyWithRetry(() => import('./pages/errors/NotFoundPage'));
const UnauthorizedPage = lazyWithRetry(() => import('./pages/errors/UnauthorizedPage'));

/**
 * Loading Fallback Component
 */
const LoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
      <p className="text-sm text-gray-600">Loading...</p>
    </div>
  </div>
);

/**
 * Main Application Router
 */
export const AppRouter: React.FC = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />

        {/* Auth callback route - must not redirect when authenticated */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />

        {/* Account suspended page - shown when user is suspended */}
        <Route path="/account-suspended" element={<AccountSuspendedPage />} />

        {/* Onboarding routes */}
        <Route
          path="/onboarding/select-plan"
          element={
            <ProtectedRoute>
              <SelectPlanPage />
            </ProtectedRoute>
          }
        />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brands"
          element={
            <ProtectedRoute>
              <BrandsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deals"
          element={
            <ProtectedRoute>
              <DealsPage />
            </ProtectedRoute>
          }
        />
        {/* Deal details with optional tab */}
        <Route
          path="/deals/:dealId"
          element={
            <ProtectedRoute>
              <DealsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/deals/:dealId/:tab"
          element={
            <ProtectedRoute>
              <DealsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/usage"
          element={
            <ProtectedRoute>
              <UsageStatsPage />
            </ProtectedRoute>
          }
        />

        {/* Feature Request routes */}
        <Route
          path="/feature-requests"
          element={
            <ProtectedRoute>
              <FeatureRequestsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/feature-requests/my"
          element={
            <ProtectedRoute>
              <MyFeatureRequestsPage />
            </ProtectedRoute>
          }
        />

        {/* Admin routes with shared layout */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="users" element={<UserManagement />} />
          <Route path="skus" element={<SKUManagement />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route path="audit-logs" element={<AuditLogViewer />} />
          <Route path="feature-requests" element={<AdminFeatureRequestsPage />} />
        </Route>

        {/* Unauthorized page */}
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* 404 page */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};
